/**
 * usePortfolio
 *
 * React Query hooks for the Agency Brand Portfolio page.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  portfolioService,
  type SaveStatusUpdatePayload,
  type CreatePortfolioViewPayload,
  type UpdatePortfolioViewPayload,
  type StatusUpdateMetrics,
  type PortfolioTimelineResponse,
} from '@/services/portfolio.service';
import { getTeamMembers } from '@/services/agency.service';
import type { AgencyStaffMember } from '@/data/portfolio.types';

export const usePortfolioClients = () => {
  return useQuery({
    queryKey: ['agency', 'portfolio'],
    networkMode: 'always',
    queryFn: () => portfolioService.getPortfolio(),
    staleTime: 60_000,
  });
};

/** Server-built digest (what the email would contain) for the send-digest modal preview. */
export const useDigestPreview = (audience: 'team' | 'brand', teamId: number | null, enabled: boolean) => {
  return useQuery({
    queryKey: ['agency', 'portfolio', 'digest-preview', audience, teamId],
    networkMode: 'always',
    queryFn: () => portfolioService.getDigestPreview(audience, teamId ?? undefined),
    enabled: enabled && (audience === 'team' || teamId !== null),
    staleTime: 0,
    retry: false,
  });
};

export type PortfolioHighlightsPeriod = 'today' | 'week' | 'month' | 'quarter';

export const usePortfolioHighlights = (period: PortfolioHighlightsPeriod = 'week') => {
  return useQuery({
    queryKey: ['agency', 'portfolio', 'highlights', period],
    networkMode: 'always',
    queryFn: () => portfolioService.getHighlights(period),
    staleTime: 120_000,
  });
};

export const useSaveStatusUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveStatusUpdatePayload) =>
      portfolioService.saveStatusUpdate(payload),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      // Refresh the per-team history so the modal's History panel shows the
      // just-saved update without a manual refetch.
      void queryClient.invalidateQueries({
        queryKey: ['agency', 'portfolio', 'status-history', variables.teamId],
      });
    },
  });
};

/** Fetch the recent status-update history for a single team. Enabled only when
 *  `teamId` is provided so the query doesn't fire while the modal is closed. */
export const useStatusUpdateHistory = (teamId: number | null, limit = 8) => {
  return useQuery({
    queryKey: ['agency', 'portfolio', 'status-history', teamId, limit],
    networkMode: 'always',
    queryFn: () => portfolioService.getStatusUpdateHistory(teamId as number, limit),
    enabled: teamId !== null,
    staleTime: 60_000,
  });
};

/** Recompute "posts published" auto-fill + top-post candidates for an
 *  arbitrary period. Enabled only once teamId + both bounds are known so the
 *  compose modal's initial render (before a period is resolved) doesn't fire
 *  a request with garbage params. */
export const useStatusUpdateMetrics = (
  teamId: number | null,
  startIso: string | null,
  endIso: string | null,
) => {
  return useQuery<StatusUpdateMetrics>({
    queryKey: ['status-update-metrics', teamId, startIso, endIso],
    networkMode: 'always',
    queryFn: () =>
      portfolioService.getStatusUpdateMetrics(teamId as number, startIso as string, endIso as string),
    enabled: teamId !== null && startIso !== null && endIso !== null,
    staleTime: 60_000,
  });
};

/** Fetch per-brand, per-day post buckets for the Brand Portfolio Timeline
 *  heat-map over an arbitrary [fromIso, toIso] window. Enabled only once both
 *  bounds are known AND the caller opts in (e.g. the Timeline tab is active)
 *  so the query doesn't fire while the tab is hidden. */
export const usePortfolioTimeline = (
  fromIso: string | null,
  toIso: string | null,
  timezone: string,
  enabled: boolean,
) => {
  return useQuery<PortfolioTimelineResponse>({
    queryKey: ['agency', 'portfolio', 'timeline', fromIso, toIso, timezone],
    networkMode: 'always',
    queryFn: () =>
      portfolioService.getPortfolioTimeline(fromIso as string, toIso as string, timezone),
    enabled: enabled && !!fromIso && !!toIso,
    staleTime: 60_000,
    // No explicit `retry` override — the QueryClient's global `smartRetry`
    // (App.tsx) already skips retrying non-retryable 4xx responses (e.g. an
    // over-92-day custom range), so an invalid window fails fast without
    // burning requests, while transient 5xx/network errors still get the
    // standard capped retry.
  });
};

// ── Agency staff ──────────────────────────────────────────────────────────────
// Adapter: maps backend TeamMember → AgencyStaffMember shape used across the
// Portfolio page (owner filter, workload rows, inbox lead avatars, etc.)

const STAFF_AVATAR_PALETTE = [
  'bg-primary-500',
  'bg-primary-700',
  'bg-secondary-400',
  'bg-coral-500',
  'bg-peach',
  'bg-soft-mint',
  'bg-primary-400',
  'bg-coral-400',
] as const;

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  user: 'Member',
};

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 0) return '??';
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

// ── Saved views CRUD ──────────────────────────────────────────────────────────

const PORTFOLIO_VIEWS_KEY = ['agency', 'portfolio', 'views'] as const;

export const usePortfolioViews = () => {
  return useQuery({
    queryKey: PORTFOLIO_VIEWS_KEY,
    networkMode: 'always',
    queryFn: () => portfolioService.listViews(),
    staleTime: 60_000,
  });
};

export const useCreatePortfolioView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePortfolioViewPayload) => portfolioService.createView(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PORTFOLIO_VIEWS_KEY });
    },
  });
};

export const useUpdatePortfolioView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdatePortfolioViewPayload }) =>
      portfolioService.updateView(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PORTFOLIO_VIEWS_KEY });
    },
  });
};

export const useDeletePortfolioView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => portfolioService.deleteView(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PORTFOLIO_VIEWS_KEY });
    },
  });
};

export const useAgencyStaff = () => {
  return useQuery<AgencyStaffMember[]>({
    queryKey: ['agency', 'team-members'],
    networkMode: 'always',
    queryFn: async () => {
      const members = await getTeamMembers();
      return members
        .filter((m) => m.status !== 'inactive')
        .map<AgencyStaffMember>((m) => ({
          id: m.id,
          name: m.fullName || m.email,
          initials: initialsFrom(m.fullName || m.email),
          avatarColor: STAFF_AVATAR_PALETTE[m.id % STAFF_AVATAR_PALETTE.length],
          role: ROLE_LABEL[m.role] ?? 'Member',
          email: m.email,
          profilePhotoUrl: m.profilePhotoUrl,
        }));
    },
    staleTime: 120_000,
  });
};
